/**
 * Deterministic pseudo-random source for the texture layers.
 *
 * The grain and hex-mesh layers are generated, not drawn, so they have to be
 * regenerable: `Math.random()` reshuffles the mesh on every re-render, which
 * reads as the background flickering. A seed makes a given layer the same
 * layer every time it is built.
 *
 * mulberry32 — 32-bit state, no dependency, uniform enough for scattering
 * points. Not for anything security-adjacent; this is decoration.
 *
 * `seed` is a 32-bit unsigned integer. Anything else is coerced by `>>> 0`
 * rather than rejected, so 1.5 draws the seed-1 stream and NaN draws the
 * seed-0 stream — two layers asked for under different seeds would come out
 * identical, with nothing raised. Callers deriving a seed must hand over a
 * whole number.
 */
export function createPrng(seed: number): () => number {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    // >>> 0 before the divide: `^` yields a signed int32, so without it every
    // draw with the high bit set comes back negative and leaves [0,1).
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}
